import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { StorageLoader } from './storageLoader';
import { ToastLoader } from './toastLoader';

export type On401Behavior = 'teardown' | 'toastOnly' | 'defer';

export interface FilterOptions {
  silent?: boolean;
  on401?: On401Behavior;
}

@Injectable({
  providedIn: 'root',
})
export class HttpResponseLoader {
  constructor(
    private navCtrl: NavController,
    private storageLoader: StorageLoader,
    private toastLoader: ToastLoader,
  ) {}

  private isOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  async filter(responseObj: any, options: FilterOptions = {}) {
    try {
      const response: any = await firstValueFrom(responseObj);

      const body = response?.body ?? response;
      const status = response?.status ?? 200;

      if (status === 201 && body?.message && !options.silent) {
        this.toastLoader.show(status, body.message);
      }

      if (body?.success === true) {
        return body.data;
      }

      if (status === 201 || status === 200) {
        return body;
      }

      throw response;
    } catch (error: any) {
      const backendError = error?.error?.error;

      const backendMessage =
        backendError?.message || error?.error?.message || '';

      const status = error?.status || 0;
      const isOfflineFailure = status === 0 && this.isOffline();
      const on401 = options.on401 ?? 'teardown';
      const deferredAuthFailure = status === 401 && on401 === 'defer';

      if (!options.silent && !isOfflineFailure && !deferredAuthFailure) {
        if (backendMessage) {
          this.toastLoader.show(status, backendMessage);
        } else {
          this.toastLoader.show(status);
        }
      }

      if (status === 401 && on401 === 'teardown') {
        await this.endSession({ silent: true });
      }

      if (isOfflineFailure) {
        const offlineError: any = new Error('offline');
        offlineError.isOffline = true;
        offlineError.status = 0;
        throw offlineError;
      }

      throw error;
    }
  }

  async endSession(options: { silent?: boolean } = {}): Promise<void> {
    if (!options.silent) {
      this.toastLoader.show(401);
    }
    await this.storageLoader.clear();
    await this.navCtrl.navigateRoot(['/login']);
  }
}
